import { translate, Translate, TranslationKey } from '@/shared/i18n';

export enum PaymentErrorCode {
  AuthenticationFailed = 'authentication_failed',
  AuthenticationNotHandled = 'authentication_not_handled',
  Blocked = 'blocked',
  CallIssuer = 'call_issuer',
  CardVelocityExceeded = 'card_velocity_exceeded',
  DoNotHonor = 'do_not_honor',
  ExpiredCard = 'expired_card',
  GenericDecline = 'generic_decline',
  IncorrectCvc = 'incorrect_cvc',
  IncorrectNumber = 'incorrect_number',
  InsufficientFunds = 'insufficient_funds',
  InvalidAccount = 'invalid_account',
  InvalidAmount = 'invalid_amount',
  InvalidCvc = 'invalid_cvc',
  InvalidPin = 'invalid_pin',
  LostCard = 'lost_card',
  PickupCard = 'pickup_card',
  RestrictedCard = 'restricted_card',
  RevocationOfAllAuthorizations = 'revocation_of_all_authorizations',
  RevocationOfAuthorization = 'revocation_of_authorization',
  StolenCard = 'stolen_card',
  TransactionNotAllowed = 'transaction_not_allowed',
  TryAgainLater = 'try_again_later',
}

type PaymentErrorContent = {
  buttonText: string;
  message: string;
};

type PaymentErrorTranslation = {
  buttonTextKey: TranslationKey;
  messageKey: TranslationKey;
};

const INSUFFICIENT_FUNDS_ERROR: PaymentErrorTranslation = {
  buttonTextKey: 'billing.error.retryPayment',
  messageKey: 'billing.error.insufficient',
};

const BANK_DECLINED_ERROR: PaymentErrorTranslation = {
  buttonTextKey: 'billing.error.retryPayment',
  messageKey: 'billing.error.bankDeclined',
};

const AUTHENTICATION_FAILED_ERROR: PaymentErrorTranslation = {
  buttonTextKey: 'billing.error.retryPayment',
  messageKey: 'billing.error.authentication',
};

const CARD_DETAILS_INCORRECT_ERROR: PaymentErrorTranslation = {
  buttonTextKey: 'billing.error.editPayment',
  messageKey: 'billing.error.cardDetails',
};

const CARD_CANNOT_BE_USED_ERROR: PaymentErrorTranslation = {
  buttonTextKey: 'billing.error.changePayment',
  messageKey: 'billing.error.cardCannotBeUsed',
};

export const PAYMENT_ERROR_CONTENT: Record<PaymentErrorCode, PaymentErrorTranslation> = {
  [PaymentErrorCode.AuthenticationFailed]: AUTHENTICATION_FAILED_ERROR,
  [PaymentErrorCode.AuthenticationNotHandled]: AUTHENTICATION_FAILED_ERROR,
  [PaymentErrorCode.Blocked]: BANK_DECLINED_ERROR,
  [PaymentErrorCode.CallIssuer]: BANK_DECLINED_ERROR,
  [PaymentErrorCode.CardVelocityExceeded]: INSUFFICIENT_FUNDS_ERROR,
  [PaymentErrorCode.DoNotHonor]: BANK_DECLINED_ERROR,
  [PaymentErrorCode.ExpiredCard]: CARD_DETAILS_INCORRECT_ERROR,
  [PaymentErrorCode.GenericDecline]: BANK_DECLINED_ERROR,
  [PaymentErrorCode.IncorrectCvc]: CARD_DETAILS_INCORRECT_ERROR,
  [PaymentErrorCode.IncorrectNumber]: CARD_DETAILS_INCORRECT_ERROR,
  [PaymentErrorCode.InsufficientFunds]: INSUFFICIENT_FUNDS_ERROR,
  [PaymentErrorCode.InvalidAccount]: CARD_CANNOT_BE_USED_ERROR,
  [PaymentErrorCode.InvalidAmount]: INSUFFICIENT_FUNDS_ERROR,
  [PaymentErrorCode.InvalidCvc]: CARD_DETAILS_INCORRECT_ERROR,
  [PaymentErrorCode.InvalidPin]: CARD_DETAILS_INCORRECT_ERROR,
  [PaymentErrorCode.LostCard]: CARD_CANNOT_BE_USED_ERROR,
  [PaymentErrorCode.PickupCard]: CARD_CANNOT_BE_USED_ERROR,
  [PaymentErrorCode.RestrictedCard]: BANK_DECLINED_ERROR,
  [PaymentErrorCode.RevocationOfAllAuthorizations]: CARD_CANNOT_BE_USED_ERROR,
  [PaymentErrorCode.RevocationOfAuthorization]: CARD_CANNOT_BE_USED_ERROR,
  [PaymentErrorCode.StolenCard]: CARD_CANNOT_BE_USED_ERROR,
  [PaymentErrorCode.TransactionNotAllowed]: BANK_DECLINED_ERROR,
  [PaymentErrorCode.TryAgainLater]: AUTHENTICATION_FAILED_ERROR,
};

const DEFAULT_ERROR_CONTENT: PaymentErrorTranslation = {
  buttonTextKey: 'billing.error.retryPayment',
  messageKey: 'billing.error.failed',
};

const translateToEnglish: Translate = (key, variables) => translate('en', key, variables);

export const getPaymentErrorContent = (
  code?: null | string,
  t: Translate = translateToEnglish,
): PaymentErrorContent => {
  const errorContent = code
    ? PAYMENT_ERROR_CONTENT[code as PaymentErrorCode] || DEFAULT_ERROR_CONTENT
    : DEFAULT_ERROR_CONTENT;

  return {
    buttonText: t(errorContent.buttonTextKey),
    message: t(errorContent.messageKey),
  };
};
